import classNames from 'classnames/bind';
import React, { ReactNode } from 'react';
import { ContentTypeTag } from '@creditsights/styleguide-web';
import { EXTERNAL_ROUTES } from '../../../../Base.constants';
import { Analyst } from '../../../../models';
import { getAnalystId } from '../../../../utils/tagUtils';
import ExternalMfeLink from '../../../MfeLink/ExternalMfeLink';
import styles from './AnalystsContent.module.scss';

const cx = classNames.bind(styles);

interface AnalystsContentProps {
  data: Analyst[];
  hideDeactivatedAnalyst?: boolean;
}

const AnalystContainer = ({ analyst, children }: { analyst: Analyst; children: ReactNode }) => {
  const analystId = getAnalystId(analyst.tagId ?? '');

  return analyst.hasBio ? (
    <ExternalMfeLink
      route={EXTERNAL_ROUTES.analysts(analystId)}
      className={cx('analyst-section-layout', 'analyst-margin')}
    >
      {children}
    </ExternalMfeLink>
  ) : (
    <div className={cx('analyst-section-layout', 'analyst-margin')}>{children}</div>
  );
};

const AnalystsContent = ({ data, hideDeactivatedAnalyst }: AnalystsContentProps): React.ReactElement => {
  return (
    <section data-testid="analysts-content" className={styles.content}>
      {data.map((analyst, i) =>
        hideDeactivatedAnalyst && !analyst.isActiveAuthor ? null : (
          <AnalystContainer analyst={analyst} key={`${analyst.initials}${analyst.displayName + i}`}>
            <div className={cx('analyst-section-layout-info')}>
              <div className={cx('img-container')}>
                {analyst.isActiveAuthor && analyst.imageUrl ? (
                  <img
                    data-testid="analyst-image"
                    alt=""
                    src={analyst.imageUrl}
                    className={cx('initials-image-bg', 'analyst-image')}
                  />
                ) : (
                  <span data-testid="initials" className={cx('initials', 'analyst-image')}>
                    {analyst.initials.toUpperCase()}{' '}
                  </span>
                )}
              </div>
              <div className={cx('analyst-layout', { 'no-role': !analyst.role || !analyst.isActiveAuthor })}>
                <div className={cx('analyst-display-name', { 'is-link': analyst.hasBio })} data-testid="analyst-title">
                  {analyst.displayName}
                </div>
                {analyst.isActiveAuthor && (
                  <div className={cx('analyst-role')} data-testid="analyst-subtitle">
                    {analyst.role}
                  </div>
                )}
              </div>
            </div>
            <ContentTypeTag contentType={analyst?.positions ? analyst.positions[0].department : ''} />
          </AnalystContainer>
        )
      )}
    </section>
  );
};

export default AnalystsContent;
